
import axiosInstance from '../axiosInstance';
import { useCallback, useEffect, useRef, useState } from "react";
import { Pagination } from "react-bootstrap";
import { FaDownload, FaEye } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { API_URL_All } from "../api";
import * as general from '../libs/general';
import * as XLSX from "xlsx";
import UserLayout from '../components/UserLayout';
import Paging from '../components/Paging';
import DataTable from '../components/ui/DataTable';
import { CheckingTicket } from '../components/tickets/Tickets';
import NormalReconciliationReview from '../components/orders/NormalReconciliationReview';
import { canViewChildFeature, RECONCILE_NORMAL_COLUMNS } from '../libs/childUserPermission';

const ACTIVE_TAB_SESSION_KEY = "userReconciliationActiveTab";
const LEGACY_PAGE_SESSION_KEY = "userReconciliationLegacyPage";
const ITEMS_PER_PAGE = 50;

function UserReconciliation() {
  const navigate = useNavigate();
  const hasInvoiceRun = useRef(false);
  const config = general.getLocalStorage("user_config");
  const showColName = canViewChildFeature(config, RECONCILE_NORMAL_COLUMNS.NAME);
  const showColOrderCount = canViewChildFeature(config, RECONCILE_NORMAL_COLUMNS.ORDER_COUNT);
  const showColFee = canViewChildFeature(config, RECONCILE_NORMAL_COLUMNS.FEE);
  const showColStatus = canViewChildFeature(config, RECONCILE_NORMAL_COLUMNS.STATUS);

  const [activeTab, setActiveTab] = useState(() => {
    const saved = sessionStorage.getItem(ACTIVE_TAB_SESSION_KEY);
    return saved === "legacy" ? "legacy" : "invoices";
  });

  const [pageInvoiceData, setPageInvoiceData] = useState({ content: [] });
  const [params, setParams] = useState({});
  const [message, setMessage] = useState("");
  const [checkingReviewData, setCheckingReviewData] = useState(null);

  const [legacyError, setLegacyError] = useState("");
  const [legacyData, setLegacyData] = useState([]);
  const [legacySearchTerm, setLegacySearchTerm] = useState("");
  const [legacyPage, setLegacyPage] = useState(() => {
    const savedPage = Number(sessionStorage.getItem(LEGACY_PAGE_SESSION_KEY));
    return Number.isInteger(savedPage) && savedPage > 0 ? savedPage : 1;
  });
  const [legacyTotalPages, setLegacyTotalPages] = useState(0);
  const [legacyTotalItems, setLegacyTotalItems] = useState(0);

  useEffect(() => {
    sessionStorage.setItem(ACTIVE_TAB_SESSION_KEY, activeTab);
  }, [activeTab]);

  useEffect(() => {
    sessionStorage.setItem(LEGACY_PAGE_SESSION_KEY, legacyPage);
  }, [legacyPage]);

  const searchInvoices = async (filter) => {
    if (window.callingUserNormalInvoice) return;
    window.callingUserNormalInvoice = true;

    var nextParams = general.getSession('user_normal_invoice_filter') || {};
    var formData = general.getFormData('#user_normal_invoice_filter');
    Object.assign(nextParams, formData);

    if (!hasInvoiceRun.current) {
      hasInvoiceRun.current = true;
      Object.assign(nextParams, general.getSession('user_normal_invoice_filter') || {});
    }

    if (filter) Object.assign(nextParams, filter);
    general.setSession('user_normal_invoice_filter', nextParams);
    setParams(nextParams);

    var filteredParams = Object.fromEntries(
      Object.entries(nextParams).filter(
        ([key, v]) => v !== undefined && v !== '' && key + v !== 'page0' && key + v !== 'size550'
      )
    );

    setMessage("");
    try {
      const response = await axiosInstance.get('/api/invoices/searchNormalInvoices', { params: filteredParams });
      setPageInvoiceData(response.data);
    } catch (error) {
      if (error.status === 401) setTimeout(navigate, 2000, '/dang-nhap');
      setPageInvoiceData({ content: [] });
      setMessage(error.message);
      console.error(error);
    } finally {
      window.callingUserNormalInvoice = false;
    }
  };

  const searchLegacyFiles = useCallback(async (search, page) => {
    setLegacyError('');
    try {
      const requestParams = {
        page: Math.max(page - 1, 0),
        size: ITEMS_PER_PAGE,
      };
      if (search.trim() !== "") requestParams.keyword = search.trim();

      const response = await axiosInstance.get(`${API_URL_All}/api/files/searchfiles`, { params: requestParams });
      const pageData = response.data.page || {};
      const fetchedData = pageData.content || [];
      const nextTotalPages = pageData.totalPages ?? 0;

      setLegacyData(fetchedData);
      setLegacyTotalPages(nextTotalPages);
      setLegacyTotalItems(pageData.totalElements ?? fetchedData.length);

      if (nextTotalPages > 0 && page > nextTotalPages) {
        setLegacyPage(nextTotalPages);
      }
    } catch (error) {
      if (error.status === 401) setTimeout(navigate, 2000, '/dang-nhap');
      console.error(error);
      setLegacyError(error.message);
      setLegacyData([]);
      setLegacyTotalPages(0);
      setLegacyTotalItems(0);
    }
  }, [navigate]);

  useEffect(() => {
    if (activeTab === "invoices") {
      searchInvoices();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "legacy") {
      searchLegacyFiles(legacySearchTerm, legacyPage);
    }
  }, [activeTab, searchLegacyFiles, legacySearchTerm, legacyPage]);

  const handleSearchChange = (e) => {
    searchInvoices({ page: 0, keyword: e.target.value });
  };

  const handleRefreshPage = () => {
    searchInvoices();
  };

  const handleChangeItemsPerPage = (e) => {
    const newItemsPerPage = parseInt(e.target.value, 10);
    searchInvoices({ page: 0, size: newItemsPerPage });
  };

  const handlePageChange = (page) => {
    searchInvoices({ page: page - 1 });
  };

  const openInvoiceDetail = (id) => {
    setCheckingReviewData({ id });
  };

  const handleExportInvoice = async (param) => {
    try {
      const requestApi = `${API_URL_All}/api/invoices/exportNormalInvoice?id=${param.id}`;
      const fileName = `Doi_soat_thuong_${param.id}.xlsx`;
      await general.downloadFile(requestApi, fileName);
    } catch (error) {
      toast.error(error.message || "Có lỗi khi tải file Excel!");
    }
  };

  const handleDownloadLegacy = (id, fileName, event) => {
    event.preventDefault();
    const url = `${API_URL_All}/api/files/download/${id}`;
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleViewLegacyExcel = async (fileId) => {
    try {
      const response = await axiosInstance.get(`${API_URL_All}/api/files/download/${fileId}`, { responseType: "arraybuffer" });
      const fileData = new Uint8Array(response.data);
      const workbook = XLSX.read(fileData, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const sheetJson = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      navigate("/view-excel", { state: { data: sheetJson } });
    } catch (error) {
      console.error("Error viewing Excel file:", error);
      toast.error("Có lỗi khi mở file Excel!", { position: "top-right" });
    }
  };

  const legacyIndexOfFirstItem = (legacyPage - 1) * ITEMS_PER_PAGE;
  const legacyLastVisibleItem = legacyIndexOfFirstItem + legacyData.length;

  return (
    <div>
      <UserLayout>
        <div className="table-responsive web-table">
          <div className="tab-container">
            <nav>
              <div className="nav nav-tabs" id="nav-tab" role="tablist">
                <button
                  type="button"
                  className={`nav-link ${activeTab === "invoices" ? "active" : ""}`}
                  onClick={() => setActiveTab("invoices")}
                >
                  Danh sách đối soát
                </button>
                <button
                  type="button"
                  className={`nav-link ${activeTab === "legacy" ? "active" : ""}`}
                  onClick={() => setActiveTab("legacy")}
                >
                  File đối soát cũ
                </button>
              </div>
            </nav>

            <div className="tab-content bg-white" id="nav-tabContent">
              {activeTab === "invoices" && (
                <div className="tab-pane fade show active">
                  <div className="container-fluid pt-2">
                    <div className="text-start mb-2">
                      <div className="row">
                        <div className="col-md-5">
                          <form id="user_normal_invoice_filter">
                            <div className="row">
                              <div className="col-md-7 height-46">
                                <div className="row">
                                  <label className="form-label col-form-label col-md-4">Tìm kiếm</label>
                                  <div className="col-md-8">
                                    <input
                                      className="form-control"
                                      type="text"
                                      name="keyword"
                                      placeholder="Nhập từ khóa..."
                                      autoComplete="off"
                                      value={params.keyword || ''}
                                      onChange={handleSearchChange}
                                    />
                                  </div>
                                </div>
                              </div>
                              <div className="col-md-5">
                                <button
                                  onClick={() => searchInvoices({ page: 0 })}
                                  type="button"
                                  className="btn btn-info btn-sm me-2 my-1"
                                >
                                  Tìm kiếm
                                </button>
                              </div>
                            </div>
                          </form>
                        </div>
                      </div>
                    </div>
                  </div>

                  <section className="p-1">
                    <DataTable maxHeight="calc(100vh - 256px)" className="table-normal">
                      <table className="table table-striped table-bordered border-normal">
                        <thead className="table-primary">
                          <tr>
                            <th width="30px" className="sticky text-center">STT</th>
                            <th className="text-center"><span>Ngày đối soát</span></th>
                            {showColName && <th className="text-start"><span>Nội dung</span></th>}
                            {showColOrderCount && <th className="text-center"><span>Số đơn</span></th>}
                            {showColFee && <th className="text-center"><span>Tổng cước</span></th>}
                            {showColStatus && <th><span>Trạng thái</span></th>}
                            <th><span>Actions</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {pageInvoiceData.page?.content?.map((item, index) => (
                            <tr key={item.id}>
                              <td className="sticky text-center" title={`id: ${item.id}`}>
                                {(pageInvoiceData.page.number * pageInvoiceData.page.size) + index + 1}
                              </td>
                              <td className="text-center" title={general.formatTimeShort(item.createdAt)}>
                                {general.formatDate(item.createdAt)}
                              </td>
                              {showColName && <td className="text-start">{item.name || item.code}</td>}
                              {showColOrderCount && <td className="text-center">{item.orderCount}</td>}
                              {showColFee && <td className="text-center">{general.formatNumber(item.fee)}</td>}
                              {showColStatus && (
                                <td>
                                  <CheckingTicket checkingId={item.checkingId} checkingName={item.checkingName} />
                                </td>
                              )}
                              <td className="text-center">
                                <span
                                  className="small-btn"
                                  title="Xuất file"
                                  onClick={() => handleExportInvoice({ id: item.id })}
                                >
                                  <FaDownload />
                                </span>
                                <span
                                  className="small-btn"
                                  title="Xem chi tiết"
                                  onClick={() => openInvoiceDetail(item.id)}
                                >
                                  xem
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                        <tbody>
                          {message === '' && pageInvoiceData.page && pageInvoiceData.page.content.length === 0 && (
                            <tr>
                              <td colSpan="7" className="text-center">Không tìm thấy dữ liệu</td>
                            </tr>
                          )}
                          {message !== '' && (
                            <tr>
                              <td colSpan="7" className="text-center">{message}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </DataTable>
                  </section>

                  <div className="my-1 d-flex justify-content-end align-items-center">
                    <Paging
                      pageData={pageInvoiceData}
                      onPageChange={handlePageChange}
                      onItemsPerPageChange={handleChangeItemsPerPage}
                    />
                  </div>
                </div>
              )}

              {activeTab === "legacy" && (
                <div className="tab-pane fade show active">
                  <div className="container-fluid pt-2">
                    <div className="row">
                      <div className="col-md-5">
                        <div className="row align-items-center">
                          <label className="form-label col-form-label col-md-4">Tìm kiếm</label>
                          <div className="col-md-8">
                            <input
                              className="form-control"
                              type="text"
                              placeholder="Tìm kiếm file cũ..."
                              value={legacySearchTerm}
                              onChange={(e) => {
                                setLegacySearchTerm(e.target.value);
                                setLegacyPage(1);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <section className="p-1">
                    <DataTable maxHeight="calc(100vh - 256px)" className="table-normal">
                      <table className="table table-striped table-bordered border-normal">
                        <thead className="table-primary">
                          <tr>
                            <th width="30px" className="sticky text-center">STT</th>
                            <th className="text-start"><span>Tên file</span></th>
                            <th className="text-center"><span>Số đơn</span></th>
                            <th className="text-center"><span>Tổng cước</span></th>
                            <th className="text-center"><span>Ngày tạo</span></th>
                            <th><span>Trạng thái</span></th>
                            <th><span>Actions</span></th>
                          </tr>
                        </thead>
                        <tbody>
                          {legacyData.length > 0 ? (
                            legacyData.map((item, index) => (
                              <tr key={item.id}>
                                <td className="sticky text-center">{legacyIndexOfFirstItem + index + 1}</td>
                                <td className="text-start">
                                  <button
                                    type="button"
                                    onClick={(event) => handleDownloadLegacy(item.id, item.fileName, event)}
                                    style={{ textDecoration: "none", color: "blue", cursor: "pointer", background: "none", border: 0, padding: 0 }}
                                  >
                                    {item.fileName}
                                  </button>
                                </td>
                                <td className="text-center">{item.orderCount?.toLocaleString("vi-VN")}</td>
                                <td className="text-center">{general.formatNumber(item.totalFreightCost)}</td>
                                <td className="text-center">{general.formatDate(item.createdAt)}</td>
                                <td>
                                  <CheckingTicket
                                    checkingId={item.registered === false ? 14 : 15}
                                    checkingName={item.registered === false ? "Vào phiếu" : "Đã đối soát"}
                                  />
                                </td>
                                <td className="text-center">
                                  <span className="small-btn" title="Tải file" onClick={(e) => handleDownloadLegacy(item.id, item.fileName, e)}>
                                    <FaDownload />
                                  </span>
                                  <span className="small-btn" title="Xem Excel" onClick={() => handleViewLegacyExcel(item.id)}>
                                    <FaEye />
                                  </span>
                                </td>
                              </tr>
                            ))
                          ) : legacyError === '' ? (
                            <tr>
                              <td colSpan="7" className="text-center">Không tìm thấy dữ liệu</td>
                            </tr>
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">{legacyError}</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </DataTable>
                  </section>

                  <div className="my-2 d-flex justify-content-between align-items-center px-2">
                    <span className="small text-muted">
                      {legacyTotalItems > 0 ? `${legacyIndexOfFirstItem + 1}–${legacyLastVisibleItem} / ${legacyTotalItems}` : "0 mục"}
                    </span>
                    <Pagination className="justify-content-end mb-0">
                      <Pagination.Prev
                        onClick={() => setLegacyPage((prev) => Math.max(prev - 1, 1))}
                        disabled={legacyPage <= 1}
                      />
                      <Pagination.Item active={legacyPage === 1} onClick={() => setLegacyPage(1)}>1</Pagination.Item>
                      {legacyPage > 4 && <Pagination.Ellipsis disabled />}
                      {[...Array(legacyTotalPages)].map((_, index) => {
                        const pageNumber = index + 1;
                        if (pageNumber > 1 && pageNumber < legacyTotalPages && Math.abs(pageNumber - legacyPage) <= 2) {
                          return (
                            <Pagination.Item key={pageNumber} active={legacyPage === pageNumber} onClick={() => setLegacyPage(pageNumber)}>
                              {pageNumber}
                            </Pagination.Item>
                          );
                        }
                        return null;
                      })}
                      {legacyPage < legacyTotalPages - 3 && <Pagination.Ellipsis disabled />}
                      {legacyTotalPages > 1 && (
                        <Pagination.Item active={legacyPage === legacyTotalPages} onClick={() => setLegacyPage(legacyTotalPages)}>
                          {legacyTotalPages}
                        </Pagination.Item>
                      )}
                      <Pagination.Next
                        onClick={() => setLegacyPage((prev) => Math.min(prev + 1, legacyTotalPages || 1))}
                        disabled={legacyTotalPages <= 1 || legacyPage >= legacyTotalPages}
                      />
                    </Pagination>
                  </div>
                </div>
              )}
            </div>
          </div>

          <input type="hidden" id="list_rateStatus" value="Vào phiếu,Đã đối soát" />

          {checkingReviewData && (
            <NormalReconciliationReview
              key={checkingReviewData.id}
              data={checkingReviewData}
              readOnly
              canEdit={false}
              canDelete={false}
              handleSearch={handleRefreshPage}
              onClose={() => setCheckingReviewData(null)}
            />
          )}
        </div>
      </UserLayout>
    </div>
  );
}

export default UserReconciliation;
